<template>
  <div class="blog-post-view landing-page">
    <div class="content-wrapper">
      <section class="section">
        <div class="container">
          <button
            class="button is-light mb-5"
            @click="$router.push({ name: 'blog' })"
          >
            <i class="fas fa-arrow-left mr-2" /> {{ $t('blog.backToList') }}
          </button>
          <div class="columns is-variable is-8">
            <div class="column is-8-desktop is-12-tablet">
              <div
                v-if="post"
                class="blog-post-content animate"
              >
                <div class="blog-image-wrapper mb-4">
                  <figure :class="imageBannerClass">
                    <img
                      onerror="this.src = '/assets/images/blog.jpg'"
                      :src="post.image || defaultImage"
                      :alt="post.title"
                      class="blog-featured-image"
                      loading="lazy"
                    >
                    <span
                      v-if="post.category"
                      class="tag is-info blog-category-badge"
                    >
                      {{ $i18n.locale === 'es' ? post.category.name_es : post.category.name }}
                    </span>
                    <div class="blog-image-gradient" />
                  </figure>
                </div>
                <h1 class="title is-2 mb-2 blog-title">
                  {{ $i18n.locale === 'es' ? post.title_es : post.title }}
                </h1>
                <div class="is-flex is-align-items-center mb-4 blog-meta-row">
                  <figure class="image is-32x32 mr-2">
                    <img
                      onerror="this.src = '/assets/images/img-placeholder.jpg'"
                      :src="post.author?.profile_image"
                      :alt="post.author?.name"
                      class="is-rounded"
                    >
                  </figure>
                  <span class="has-text-weight-semibold">{{ post.author?.name }}</span>
                  <span class="mx-2">·</span>
                  <span class="has-text-grey is-size-7">{{ formatDate(post.publishing_date) }}</span>
                </div>
                <div
                  class="content blog-content"
                  v-html="$i18n.locale === 'es' ? post.content_es : post.content"
                />
              </div>
              <div
                v-else
                class="has-text-centered py-6"
              >
                <i class="fas fa-spinner fa-spin fa-2x mb-4" />
                <p>{{ $t('blog.loading') }}</p>
              </div>
            </div>

            <!-- Similar articles sidebar -->
            <div class="column is-4-desktop is-12-tablet">
              <div
                v-if="similarArticles.length"
                class="similar-articles-card"
              >
                <h3 class="title is-5 mb-4">
                  {{ $t('blog.similarArticles') }}
                </h3>
                <div class="similar-articles-list">
                  <router-link
                    v-for="article in similarArticles"
                    :key="article._id"
                    :to="{ name: 'blog-post', params: { id: article.key } }"
                    class="similar-article-item"
                  >
                    <img
                      :src="article.image || defaultImage"
                      :alt="article.title"
                      class="similar-article-thumb"
                    >
                    <div class="similar-article-info">
                      <div class="similar-article-title">
                        {{ $i18n.locale === 'es' ? article.title_es : article.title }}
                      </div>
                      <div class="similar-article-description">
                        {{ $i18n.locale === 'es' ? article.description_es : article.description }}
                      </div>
                      <div class="similar-article-date">
                        {{ formatDate(article.publishing_date) }}
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import blogService from '@/services/blogService';

const defaultImage = ref('/assets/images/blog.jpg');
const { locale } = useI18n();
const route = useRoute();
const post = ref(null);
const similarArticles = ref([]);


// Responsive aspect ratio for image banner
const isMobile = ref(false);
if (typeof window !== 'undefined') {
  const checkMobile = () => {
    isMobile.value = window.innerWidth <= 900;
  };
  checkMobile();
  window.addEventListener('resize', checkMobile);
}
const imageBannerClass = computed(() => isMobile.value ? 'image is-3by1 blog-image-banner' : 'image is-2by1 blog-image-banner');

const formatDate = (date) => {
  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const getRandomImage = () => {
  const random = Math.floor(Math.random() * 5) + 1;
  return `/assets/images/blog-${random}.jpg`;
};

// Function to update meta tags directly
const updateMetaTags = (postData) => {
  if (!postData) return;


  const title = `${locale.value === 'es' ? postData.title_es : postData.title} | Indiefy Blog`;
  const description = locale.value === 'es' ? postData.description_es : postData.description;
  const image = postData.image || defaultImage.value;

  // Update document title
  document.title = title;

  // Update meta tags
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  }

  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', title);
  }

  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) {
    ogDescription.setAttribute('content', description);
  }

  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage) {
    ogImage.setAttribute('content', image);
  }

  const ogType = document.querySelector('meta[property="og:type"]');
  if (ogType) {
    ogType.setAttribute('content', 'article');
  }
};

async function fetchPostAndSimilar(id) {
  const res = await blogService.get(id);
  res.image = res.image || getRandomImage();
  post.value = res;

  // Update meta tags after fetching post data
  updateMetaTags(post.value);

  // Fetch similar articles (by category or recent)
  const params = { limit: 5, published: 1 };

  if (post.value.category?.key) params.category = post.value.category.key;

  const similarRes = await blogService.getAll(params);

  similarRes.items.forEach(item => {
    if (!item.image) {
      item.image = getRandomImage();
    }
  });

  // Exclude current post
  similarArticles.value = (similarRes.items || []).filter(a => a._id !== post.value._id).slice(0, 4);
}

onBeforeMount(() => {
  // Set initial meta tags
  document.title = 'Blog | Indiefy';
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', 'Indiefy Blog');
  }
});

onMounted(() => {
  fetchPostAndSimilar(route.params.id);
});

watch(() => route.params.id, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchPostAndSimilar(newId);
  }
});

// Initial useHead setup
useHead({
  title: 'Blog | Indiefy',
  meta: [
    { name: 'description', content: 'Indiefy Blog' },
    { property: 'og:title', content: 'Blog | Indiefy' },
    { property: 'og:description', content: 'Indiefy Blog' },
    { property: 'og:type', content: 'website' },
  ],
});
</script>

<style scoped lang="scss">
@import '@/styles/abstracts/colors';

.blog-post-content {
  max-width: 820px;
  margin: 0 auto;
  background: var(--color-card-background, #fff);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  padding: 2.5rem 2.5rem 2rem 2.5rem;
  position: relative;
  animation: fade-in 0.7s cubic-bezier(0.4,0,0.2,1);
  .blog-image-wrapper {
    position: relative;
    .blog-image-banner {
      margin-bottom: 0;
      img.blog-featured-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 12px 12px 0 0;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      }
      .blog-category-badge {
        position: absolute;
        top: 1.2rem;
        left: 1.2rem;
        z-index: 2;
        font-size: 0.95em;
        font-weight: 700;
        background: linear-gradient(90deg, $indiefy-blue, $indiefy-lavender);
        color: #fff;
        border: none;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      }
      .blog-image-gradient {
        position: absolute;
        left: 0; right: 0; bottom: 0; height: 80px;
        background: linear-gradient(0deg, rgba(30,30,40,0.18) 60%, transparent 100%);
        z-index: 1;
        border-radius: 0 0 12px 12px;
      }
    }
  }
  .blog-title {
    font-size: 2.1rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    color: var(--color-primary, #7189FF);
    line-height: 1.15;
    letter-spacing: -1px;
  }
  .blog-meta-row {
    font-size: 1.05rem;
    .is-rounded {
      border-radius: 50%;
    }
  }
  .blog-content {
    font-size: 1.18rem;
    line-height: 1.75;
    color: var(--color-text, #222);
    margin-top: 2.5rem;
    img {
      max-width: 100%;
      border-radius: 8px;
      margin: 1.5rem 0;
      box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    }
    h2, h3, h4 {
      margin-top: 2.2rem;
      margin-bottom: 1.1rem;
      font-weight: 700;
      color: var(--color-primary, #7189FF);
    }
    ul, ol {
      margin-left: 2rem;
      margin-bottom: 1.5rem;
    }
    blockquote {
      border-left: 4px solid var(--color-primary, #6366f1);
      padding-left: 1.2rem;
      color: #666;
      font-style: italic;
      margin: 1.5rem 0;
      background: rgba(113,137,255,0.06);
      border-radius: 0 8px 8px 0;
    }
    a {
      color: var(--color-primary, #6366f1);
      text-decoration: underline;
      transition: color 0.2s;
      &:hover {
        text-decoration: none;
        color: $indiefy-lavender;
      }
    }
    code {
      background: #f5f5f5;
      color: #3e8ed0;
      border-radius: 4px;
      padding: 0.2em 0.5em;
      font-size: 0.98em;
    }
  }
}

@media (max-width: 900px) {
  .blog-post-content {
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;

    .blog-image-wrapper {
      margin: 0;
      position: relative;

      .blog-image-banner {
        border-radius: 0;
        margin: 0;
        height: 280px;
        position: relative;
        overflow: hidden;

        img.blog-featured-image {
          border-radius: 0;
          height: 280px;
          width: 100%;
          object-fit: cover;
        }
      }
    }

    .post-header-content {
      position: relative;
      margin-top: -280px;
      padding: 1rem;
      background: linear-gradient(0deg, var(--color-background, #fff) 20%, rgba(0,0,0,0) 100%);
      min-height: 280px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      .blog-category-badge {
        align-self: flex-start;
        margin-bottom: auto;
        background: linear-gradient(90deg, $indiefy-blue, $indiefy-lavender);
        color: #fff;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 2rem;
        font-weight: 600;
        font-size: 0.9rem;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      }

      .blog-title {
        color: var(--color-text, #000);
        font-size: 1.75rem;
        line-height: 1.3;
        margin: 0 0 1rem 0;
        padding: 0;
        font-weight: 800;
        letter-spacing: -0.5px;
      }

      .blog-meta-row {
        color: var(--color-text, #000);
        margin: 0 0 0.5rem 0;
        padding: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;

        .is-rounded {
          width: 32px;
          height: 32px;
          border: 2px solid var(--color-background, #fff);
        }

        span {
          font-size: 0.95rem;
          color: var(--color-text-light, #666);
        }
      }
    }

    .blog-content {
      background: var(--color-background, #fff);
      padding: 2rem 1rem;
      margin-top: 0;

      font-size: 1.01rem;
      line-height: 1.7;

      h1, h2, h3, h4 {
        margin-top: 1.5rem;
        margin-bottom: 0.8rem;
        font-size: 1.25rem;
        font-weight: 700;
        color: var(--color-primary, #7189FF);
      }

      p {
        margin-bottom: 1.1rem;
        font-size: 1.01rem;
        line-height: 1.7;
      }

      ul, ol {
        margin-left: 1.1rem;
        margin-bottom: 1rem;
      }

      blockquote {
        padding-left: 0.7rem;
        margin: 1rem 0;
        font-size: 0.98rem;
      }

      img {
        margin: 1.5rem -1rem;
        width: calc(100% + 2rem);
        max-width: none;
        border-radius: 0;
      }

      code {
        font-size: 0.93em;
      }
    }
  }

  .similar-articles-card {
    margin-top: 0;
    background: var(--color-background-alt, #f9f9f9);
    border-radius: 0;
    padding: 2rem 1rem;

    .similar-article-thumb {
      width: 72px;
      height: 72px;
      border-radius: 8px;
    }

    .similar-article-title {
      font-size: 1rem;
    }

    .similar-article-description {
      font-size: 0.9rem;
    }
  }
}

@media (max-width: 600px) {
  .blog-post-content {
    .blog-image-wrapper {
      .blog-image-banner {
        height: 240px;

        img.blog-featured-image {
          height: 240px;
        }
      }
    }

    .post-header-content {
      margin-top: -240px;
      min-height: 240px;
      padding: 1rem;

      .blog-title {
        font-size: 1.5rem;
      }

      .blog-meta-row {
        .is-rounded {
          width: 28px;
          height: 28px;
        }

        span {
          font-size: 0.9rem;
        }
      }
    }
  }

  .similar-articles-card {
    .similar-article-thumb {
      width: 60px;
      height: 60px;
    }
  }
}

.body--dark .blog-post-content {
  background: var(--color-background-alt, #181a20);
  color: var(--color-text, #fff);
  .blog-content {
    color: var(--color-text, #fff);
    blockquote {
      background: rgba(113,137,255,0.12);
      color: #bfcfff;
    }
    a {
      color: $indiefy-lavender;
    }
  }
}

.similar-articles-card {
  background: var(--color-card-background, #fff);
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 769px) {
    position: sticky;
    top: 2rem;
  }

  .title {
    color: var(--color-primary, #7189FF);
    font-weight: 700;
    font-size: 1.15rem;
    margin-bottom: 1.2rem;
  }
  .similar-articles-list {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }
  .similar-article-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    border-radius: 8px;
    background: var(--color-background, #f9f9f9);
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    padding: 0.7rem 0.9rem;
    transition: box-shadow 0.18s, background 0.18s;
    &:hover {
      background: var(--color-background-alt, #f0f4ff);
      box-shadow: 0 4px 16px rgba(113,137,255,0.08);
      text-decoration: none;
    }
    .similar-article-thumb {
      width: 56px;
      height: 56px;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.07);
      background: #f5f5f5;
      flex-shrink: 0;
    }
    .similar-article-info {
      flex: 1;
      min-width: 0;
      .similar-article-title {
        font-weight: 600;
        font-size: 1.01rem;
        color: var(--color-primary, #7189FF);
        margin-bottom: 0.3rem;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .similar-article-description {
        font-size: 0.85rem;
        color: var(--color-text-light, #666);
        margin-bottom: 0.3rem;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .similar-article-date {
        font-size: 0.8rem;
        color: var(--color-text-light, #888);
      }
    }
  }
}
</style>